"use client"

import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Props {
  limit: number;
  offset: number;
  total: number;
  onLimitChange: (limit: number) => void;
  onOffsetChange: (offset: number) => void;
  pageSizes?: number[];
}

export function Pagination({
  limit,
  offset,
  total,
  onLimitChange,
  onOffsetChange,
  pageSizes = [50, 100, 250],
}: Props) {
  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + limit, total);
  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 py-3 px-1 text-sm">
      <div className="text-muted-foreground tabular-nums">
        {total === 0 ? 'No results' : `Showing ${start}–${end} of ${total}`}
      </div>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          Page size
          <select
            value={limit}
            onChange={(e) => {
              onLimitChange(Number(e.target.value));
              onOffsetChange(0);
            }}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm"
          >
            {pageSizes.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <Button
          variant="outline"
          size="sm"
          disabled={!canPrev}
          onClick={() => onOffsetChange(Math.max(0, offset - limit))}
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Prev
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!canNext}
          onClick={() => onOffsetChange(offset + limit)}
        >
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
